// Normalized TX schema: { hash, timestamp, type, token, amount, counterparty, explorerUrl, status }

// ─── Tron ────────────────────────────────────────────────────────────────────
export async function fetchTronTrc20Txs(address, limit = 200) {
  const url = `https://api.trongrid.io/v1/accounts/${address}/transactions/trc20?limit=${limit}&only_confirmed=true`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  const data = await res.json();
  return (data.data || []).map(tx => normalizeTronTrc20Tx(tx, address));
}

function normalizeTronTrc20Tx(tx, myAddress) {
  const decimals = parseInt((tx.token_info && tx.token_info.decimals) || '6');
  const amount = parseFloat(tx.value || '0') / Math.pow(10, decimals);
  const type = tx.from && tx.from.toLowerCase() === myAddress.toLowerCase() ? 'OUT' : 'IN';
  return {
    hash: tx.transaction_id,
    timestamp: tx.block_timestamp,
    type,
    token: (tx.token_info && tx.token_info.symbol) || 'TRC20',
    amount,
    counterparty: type === 'OUT' ? tx.to : tx.from,
    explorerUrl: `https://tronscan.org/#/transaction/${tx.transaction_id}`,
    status: 'confirmed',
  };
}

export async function fetchTronTxs(address, limit = 200) {
  const url = `https://api.trongrid.io/v1/accounts/${address}/transactions?limit=${limit}&only_confirmed=true`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  const data = await res.json();
  return (data.data || []).map(tx => normalizeTronTx(tx, address));
}

function normalizeTronTx(tx, myAddress) {
  const contracts = (tx.raw_data && tx.raw_data.contract) || [];
  const c = contracts[0];
  let type = 'CONTRACT', token = '?', amount = 0, counterparty = '';

  if (c) {
    const v = (c.parameter && c.parameter.value) || {};
    if (c.type === 'TransferContract') {
      token = 'TRX';
      amount = (v.amount || 0) / 1e6;
      const ownerBase58 = toBase58(v.owner_address);
      type = ownerBase58.toLowerCase() === myAddress.toLowerCase() ? 'OUT' : 'IN';
      counterparty = type === 'OUT' ? toBase58(v.to_address) : ownerBase58;
    } else if (c.type === 'TransferAssetContract') {
      token = v.asset_name || 'TRC10';
      amount = (v.amount || 0) / 1e6;
      type = toBase58(v.owner_address).toLowerCase() === myAddress.toLowerCase() ? 'OUT' : 'IN';
      counterparty = type === 'OUT' ? toBase58(v.to_address) : toBase58(v.owner_address);
    } else if (c.type === 'TriggerSmartContract') {
      token = 'TRC20';
      type = 'CONTRACT';
    }
  }

  return {
    hash: tx.txID,
    timestamp: tx.block_timestamp,
    type,
    token,
    amount,
    counterparty,
    explorerUrl: `https://tronscan.org/#/transaction/${tx.txID}`,
    status: 'confirmed',
  };
}

function toBase58(hexOrStr) {
  if (!hexOrStr) return '';
  if (hexOrStr.startsWith('T') || hexOrStr.startsWith('4')) {
    try {
      // Try to use TronWeb if available globally (loaded by HelloWorld)
      if (typeof window !== 'undefined' && window._tronWeb) {
        return window._tronWeb.address.fromHex(hexOrStr);
      }
    } catch {}
  }
  return hexOrStr;
}

// ─── Ethereum / BSC / Polygon (Etherscan-compatible) ────────────────────────
export async function fetchEthTokenTxs(address, chainId = 1, page = 1, offset = 200) {
  const key = process.env.VUE_APP_ETHERSCAN_KEY || '';
  const url = `https://api.etherscan.io/v2/api?chainid=${chainId}&module=account&action=tokentx&address=${address}&sort=desc&page=${page}&offset=${offset}&startblock=0&endblock=99999999&apikey=${key}`;
  const res = await fetch(url);
  const data = await res.json();
  if (data.status === '0' && data.message !== 'No transactions found') {
    if (data.message === 'NOTOK') throw new Error(data.result || 'Token API error');
  }
  const txs = Array.isArray(data.result) ? data.result : [];
  return txs.map(tx => normalizeEthTokenTx(tx, address, chainId));
}

// Etherscan discontinued free-tier API access for BNB Chain specifically (Base and
// Optimism too) — chainId 56 always returns "Free API access is not supported for
// this chain" regardless of key, even though Ethereum (1) and Polygon (137) work
// fine on the same free key. BNB Chain's own recommended free replacement is
// MegaNode/BSCTrace (nodereal.io) — see fetchBscTokenHoldings below.
async function fetchEvmTokenMetaEtherscan(address, chainId) {
  const key = process.env.VUE_APP_ETHERSCAN_KEY || '';
  const url = `https://api.etherscan.io/v2/api?chainid=${chainId}&module=account&action=tokentx&address=${address}&sort=desc&page=1&offset=1000&startblock=0&endblock=99999999&apikey=${key}`;
  try {
    const res = await fetch(url);
    const data = await res.json();
    if (data.status === '0' && data.message !== 'No transactions found') return [];
    const txs = Array.isArray(data.result) ? data.result : [];
    const seen = new Map();
    for (const tx of txs) {
      const addr = (tx.contractAddress || '').toLowerCase();
      if (addr && !seen.has(addr)) {
        seen.set(addr, {
          address: tx.contractAddress,
          ticker: tx.tokenSymbol || 'UNKNOWN',
          decimals: parseInt(tx.tokenDecimal || '18'),
        });
      }
    }
    return [...seen.values()];
  } catch {
    return [];
  }
}

// MegaNode's nr_getTokenHoldings returns an address's current BEP20 holdings
// directly (symbol, decimals, balance) — no tx-history scanning needed, so this
// doubles as both discovery and a balance source, though callers only consume the
// {address, ticker, decimals} shape and re-fetch balance themselves via balanceOf
// to stay consistent with the ETH/Polygon path. Degrades to [] with no error if
// VUE_APP_MEGANODE_KEY isn't configured (free signup at nodereal.io).
async function fetchBscTokenHoldings(address) {
  const key = process.env.VUE_APP_MEGANODE_KEY || '';
  if (!key) return [];
  try {
    const res = await fetch(`https://bsc-mainnet.nodereal.io/v1/${key}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'nr_getTokenHoldings',
        params: [address, '0x0', '0x64'], // page 0, page size 100 (hex)
      }),
      signal: AbortSignal.timeout(10000),
    });
    const data = await res.json();
    const details = (data.result && data.result.details) || [];
    return details.map(t => ({
      address: t.tokenAddress,
      ticker: t.tokenSymbol || 'UNKNOWN',
      decimals: parseInt(t.tokenDecimails ?? t.tokenDecimals ?? '18', 16),
    }));
  } catch (e) {
    console.warn('MegaNode token holdings fetch failed:', e.message);
    return [];
  }
}

// Returns unique ERC20 token metadata discovered for an address (symbol + decimals).
// BSC routes through MegaNode (see above); Ethereum/Polygon still use Etherscan V2,
// which has a working free tier for those two chains.
export async function fetchEvmTokenMeta(address, chainId) {
  if (chainId === 56) return fetchBscTokenHoldings(address);
  return fetchEvmTokenMetaEtherscan(address, chainId);
}

function normalizeEthTokenTx(tx, myAddress, chainId) {
  const decimals = parseInt(tx.tokenDecimal || '18');
  const amount = parseFloat(tx.value || '0') / Math.pow(10, decimals);
  const type = tx.from.toLowerCase() === myAddress.toLowerCase() ? 'OUT' : 'IN';
  const explorerBase = chainId === 56 ? 'https://bscscan.com' : chainId === 137 ? 'https://polygonscan.com' : 'https://etherscan.io';
  return {
    hash: tx.hash,
    timestamp: parseInt(tx.timeStamp) * 1000,
    type,
    token: tx.tokenSymbol || 'TOKEN',
    amount,
    counterparty: type === 'OUT' ? tx.to : tx.from,
    explorerUrl: `${explorerBase}/tx/${tx.hash}`,
    status: 'confirmed',
  };
}

export async function fetchEthTxs(address, page = 1, offset = 50, chainId = 1) {
  const key = process.env.VUE_APP_ETHERSCAN_KEY || '';
  const url = `https://api.etherscan.io/v2/api?chainid=${chainId}&module=account&action=txlist&address=${address}&sort=desc&page=${page}&offset=${offset}&startblock=0&endblock=99999999&apikey=${key}`;
  const res = await fetch(url);
  const data = await res.json();
  if (data.status === '0' && data.message !== 'No transactions found') {
    if (data.message === 'NOTOK') throw new Error(data.result || 'API error');
  }
  const txs = Array.isArray(data.result) ? data.result : [];
  return txs.map(tx => normalizeEthTx(tx, address));
}

function normalizeEthTx(tx, myAddress) {
  const ethVal = parseFloat(tx.value || '0') / 1e18;
  const type = tx.from.toLowerCase() === myAddress.toLowerCase() ? 'OUT' : 'IN';
  return {
    hash: tx.hash,
    timestamp: parseInt(tx.timeStamp) * 1000,
    type: tx.isError === '1' ? 'FAILED' : type,
    token: 'ETH',
    amount: ethVal,
    counterparty: type === 'OUT' ? tx.to : tx.from,
    explorerUrl: `https://etherscan.io/tx/${tx.hash}`,
    status: tx.isError === '1' ? 'failed' : 'confirmed',
  };
}

// ─── Solana ──────────────────────────────────────────────────────────────────
export async function fetchSolanaTxs(address, connection, limit = 50) {
  const { PublicKey } = await import('@solana/web3.js');
  const pubkey = new PublicKey(address);
  const sigs = await connection.getSignaturesForAddress(pubkey, { limit });
  const hashes = sigs.map(s => s.signature);
  if (!hashes.length) return [];

  const parsed = await connection.getParsedTransactions(hashes, { maxSupportedTransactionVersion: 0 });
  return parsed.map((tx, i) => normalizeSolanaTx(tx, sigs[i], address));
}

function normalizeSolanaTx(tx, sigInfo, myAddress) {
  if (!tx) return {
    hash: sigInfo.signature, timestamp: (sigInfo.blockTime || 0) * 1000,
    type: 'UNKNOWN', token: 'SOL', amount: 0, counterparty: '',
    explorerUrl: `https://explorer.solana.com/tx/${sigInfo.signature}`,
    status: 'unknown',
  };

  let amount = 0, type = 'CONTRACT', counterparty = '';
  try {
    const accounts = tx.transaction.message.accountKeys.map(k =>
      typeof k === 'string' ? k : k.pubkey.toString()
    );
    const myIdx = accounts.indexOf(myAddress);
    if (myIdx >= 0 && tx.meta) {
      const pre = tx.meta.preBalances[myIdx] || 0;
      const post = tx.meta.postBalances[myIdx] || 0;
      const delta = (post - pre) / 1e9;
      amount = Math.abs(delta);
      type = delta < 0 ? 'OUT' : delta > 0 ? 'IN' : 'CONTRACT';
    }
  } catch {}

  return {
    hash: sigInfo.signature,
    timestamp: (sigInfo.blockTime || 0) * 1000,
    type,
    token: 'SOL',
    amount,
    counterparty,
    explorerUrl: `https://explorer.solana.com/tx/${sigInfo.signature}`,
    status: sigInfo.err ? 'failed' : 'confirmed',
  };
}

// ─── Bitcoin (Blockstream) ────────────────────────────────────────────────────
export async function fetchBtcTxs(address) {
  const res = await fetch(`https://blockstream.info/api/address/${address}/txs`);
  const txs = await res.json();
  return (txs || []).map(tx => normalizeBtcTx(tx, address));
}

function normalizeBtcTx(tx, myAddress) {
  let received = 0, sent = 0;
  (tx.vout || []).forEach(o => { if (o.scriptpubkey_address === myAddress) received += o.value; });
  (tx.vin || []).forEach(i => {
    if (i.prevout && i.prevout.scriptpubkey_address === myAddress) sent += i.prevout.value;
  });
  const amount = Math.abs(received - sent) / 1e8;
  const type = sent > received ? 'OUT' : received > sent ? 'IN' : 'CONTRACT';
  return {
    hash: tx.txid,
    timestamp: (tx.status && tx.status.block_time ? tx.status.block_time : 0) * 1000,
    type,
    token: 'BTC',
    amount,
    counterparty: '',
    explorerUrl: `https://blockstream.info/tx/${tx.txid}`,
    status: tx.status && tx.status.confirmed ? 'confirmed' : 'pending',
  };
}
